import resolveSalesChannelId from './resolveSalesChannelId';

/**
 * Builds per-variant media rows for a channel.
 * When `masterVariantIds` is provided, every id gets an entry (empty `assetIds` = clear/unselect).
 */
const buildVariantAssetsForChannel = (channelId, variantMediaByChannel = {}, masterVariantIds = null) => {
  const channelVariantMedia = variantMediaByChannel[channelId] || {};
  const scopedMasterVariantIds = masterVariantIds?.length
    ? masterVariantIds.map(String)
    : null;
  const byMasterVariant = new Map();

  Object.entries(channelVariantMedia).forEach(([assetId, variantIds]) => {
    (variantIds || []).forEach((masterVariantId) => {
      const id = String(masterVariantId);
      if (scopedMasterVariantIds && !scopedMasterVariantIds.includes(id)) {
        return;
      }

      const entry = byMasterVariant.get(id) || {
        masterVariantId: id,
        assetIds: [],
      };

      if (!entry.assetIds.includes(assetId)) {
        entry.assetIds.push(assetId);
      }

      byMasterVariant.set(id, entry);
    });
  });

  if (scopedMasterVariantIds) {
    return scopedMasterVariantIds.map((id) => (
      byMasterVariant.get(id) || { masterVariantId: id, assetIds: [] }
    ));
  }

  return [...byMasterVariant.values()];
};

/**
 * Which master variants get media rows (incl. empty clears).
 * - Explicit sync scope → those ids.
 * - Product-level + any variant assignment → all product variants (omit = unselect).
 * - Pure gallery / skipped variant step → null (no variantAssets; server leaves companions alone).
 */
export const resolveMediaMasterVariantIds = ({
  masterVariantIds,
  product,
  variantMediaByChannel = {},
  variantMediaStepCompleted = false,
}) => {
  if (masterVariantIds?.length) {
    return masterVariantIds.map(String);
  }

  const hasVariantAssignments = Object.values(variantMediaByChannel)
    .some((channelMap) => Object.keys(channelMap || {}).length > 0);

  if (!variantMediaStepCompleted && !hasVariantAssignments) {
    return null;
  }

  return (product?.variants || []).map(({ _id }) => String(_id));
};

export const shouldIncludeDraftMedia = (publishingScope) => Boolean(publishingScope?.media);

export const buildCreateDraftMediaForChannel = ({
  channelId,
  productMediaByChannel = {},
  variantMediaByChannel = {},
  masterVariantIds = null,
  includeGallery = true,
  mediaAltTextByChannel = {},
}) => {
  const galleryAssetIds = includeGallery ? (productMediaByChannel[channelId] || []) : [];
  const variantAssets = buildVariantAssetsForChannel(
    channelId,
    variantMediaByChannel,
    masterVariantIds,
  );
  const mediaAltText = mediaAltTextByChannel[channelId] || [];

  return {
    ...(includeGallery ? { galleryAssetIds } : {}),
    ...(variantAssets.length ? { variantAssets } : {}),
    ...(mediaAltText.length ? { mediaAltText } : {}),
  };
};

export const buildListingMediaPayload = ({
  channelIds,
  productMediaByChannel = {},
  variantMediaByChannel = {},
  salesChannels = [],
  masterVariantIds = null,
  includeGallery = true,
  mediaAltTextByChannel = {},
}) => channelIds.map((channelId) => ({
  salesChannelId: resolveSalesChannelId(channelId, salesChannels),
  ...buildCreateDraftMediaForChannel({
    channelId,
    productMediaByChannel,
    variantMediaByChannel,
    masterVariantIds,
    includeGallery,
    mediaAltTextByChannel,
  }),
})).filter((entry) => (
  Object.prototype.hasOwnProperty.call(entry, 'galleryAssetIds')
  || entry.variantAssets?.length
  || entry.mediaAltText?.length
));

export default buildCreateDraftMediaForChannel;
